import {
  type ColumnDef,
  Flex,
  FormControlSizes,
  FormMessage,
  IconButton,
  IconName,
  InlineFormattedNumber,
  NumberInput,
  type SizeColumnParams,
  Stack,
  Text,
  toBigWithDefault,
  Tooltip,
  useCallbackRef,
  useCurrency,
  useSecurity,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import { getPricingDetailsFromFixing } from '../getPricingDetailsFromFixing';
import { InitialValueText } from './InitialValueText';
import type { ModifyFixingEntry } from './modifyFixingSlice';

interface UseModifyFixingBlotterColumnsProps {
  onEntryFixingChange: (symbol: string, value: string | undefined) => void;
  onEntryPriceChange: (symbol: string, value: string | undefined) => void;
  onEntryAmountChange: (symbol: string, value: string | undefined) => void;
}

interface EntryInputCellRendererParams {
  onEntryFixingChange: UseModifyFixingBlotterColumnsProps['onEntryFixingChange'];
  onEntryPriceChange: UseModifyFixingBlotterColumnsProps['onEntryPriceChange'];
  onEntryAmountChange: UseModifyFixingBlotterColumnsProps['onEntryAmountChange'];
}

export function modifyFixingInputID(rowIndex: number | null) {
  return `modify-fixing-blotter-input-${rowIndex}`;
}

export function modifyTotalInputID(rowIndex: number | null) {
  return `modify-total-blotter-input-${rowIndex}`;
}

export const useModifyFixingBlotterColumns = ({
  onEntryFixingChange,
  onEntryPriceChange,
  onEntryAmountChange,
}: UseModifyFixingBlotterColumnsProps) => {
  const columns = useMemo(
    () =>
      compact([
        {
          type: 'security',
          field: 'symbol',
          title: 'Symbol',
          suppressKeyboardEvent: () => true,
          width: 110,
        },
        {
          type: 'fixingIndex',
          field: 'index',
          title: 'Index',
          suppressKeyboardEvent: () => true,
          width: 150,
        },
        {
          type: 'custom',
          id: 'fixing',
          width: 220,
          title: 'Fixing',
          headerClass: 'ag-right-aligned-cell',
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ModifyFixingEntry>): ModifyFixingEntry | undefined => {
              return params.data;
            },
            cellRendererParams: { onEntryFixingChange, onEntryPriceChange, onEntryAmountChange },
            cellRenderer: FixingInputCellRenderer,
          } satisfies ColDef<ModifyFixingEntry>,
        },
        {
          type: 'price',
          field: 'PricingReference',
          width: 80,
          title: 'Quote',
          suppressKeyboardEvent: () => true,
          params: {
            isReferencePrice: true,
          },
        },
        {
          type: 'custom',
          id: 'price',
          width: 220,
          title: 'Price',
          headerClass: 'ag-right-aligned-cell',
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ModifyFixingEntry>): ModifyFixingEntry | undefined => {
              return params.data;
            },
            cellRendererParams: { onEntryFixingChange, onEntryPriceChange, onEntryAmountChange },
            cellRenderer: PriceInputCellRenderer,
          } satisfies ColDef<ModifyFixingEntry>,
        },
        {
          type: 'size',
          field: 'tradedQty',
          width: 150,
          title: 'Traded Qty',
          suppressKeyboardEvent: () => true,
          params: {
            currencyField: 'currency',
            truncate: false,
          } satisfies SizeColumnParams<ModifyFixingEntry>,
        },
        {
          type: 'custom',
          id: 'amount',
          width: 220,
          title: 'Total',
          headerClass: 'ag-right-aligned-cell',
          params: {
            suppressKeyboardEvent: () => true,
            valueGetter: (params: ValueGetterParams<ModifyFixingEntry>): ModifyFixingEntry | undefined => {
              return params.data;
            },
            cellRendererParams: { onEntryFixingChange, onEntryPriceChange, onEntryAmountChange },
            cellRenderer: AmountInputCellRenderer,
          } satisfies ColDef<ModifyFixingEntry>,
        },
      ] satisfies (ColumnDef<ModifyFixingEntry> | false)[]),
    [onEntryAmountChange, onEntryFixingChange, onEntryPriceChange]
  );

  return columns;
};

function FixingInputCellRenderer({
  value,
  node,
  onEntryFixingChange,
}: ICellRendererParams<ModifyFixingEntry, ModifyFixingEntry> & EntryInputCellRendererParams) {
  const handleChange = useCallbackRef((fixingPrice: string) => {
    if (!value) {
      return;
    }

    if (!value.symbol) {
      return;
    }

    onEntryFixingChange(value.symbol, fixingPrice);
  });

  if (!value) {
    return <></>;
  }

  return (
    <InputCell
      initialValue={value.fixing}
      showInitialValue={!toBigWithDefault(value.fixingField.value, 0).eq(toBigWithDefault(value.fixing, 0))}
      currency={value.amountCurrency}
      warning={
        value.fixingField.errorString ? <FormMessage error>{value.fixingField.errorString}</FormMessage> : undefined
      }
    >
      <NumberInput
        value={value.fixingField.displayValue}
        disabled={value.fixingField.isDisabled}
        invalid={!!value.fixingField.errorString}
        onChange={handleChange}
        id={modifyFixingInputID(node.rowIndex)}
        data-testid={`modify-fixing-blotter-fixing-input-${value.rowID}`}
        suffix={value.amountCurrency}
      />
    </InputCell>
  );
}

function PriceInputCellRenderer({
  value,
  onEntryPriceChange,
}: ICellRendererParams<ModifyFixingEntry, ModifyFixingEntry> & EntryInputCellRendererParams) {
  const details = useEntryPricingDetails(value ?? undefined);
  const computedPx = details?.px;
  const priceIsOff = computedPx ? !value?.priceField.bigValue?.eq(computedPx) : false;

  const handleChange = useCallbackRef((price: string) => {
    if (!value || !value.symbol) {
      return;
    }

    onEntryPriceChange(value.symbol, price);
  });

  if (!value) {
    return <></>;
  }

  return (
    <InputCell
      initialValue={value.price}
      showInitialValue={!toBigWithDefault(value.priceField.value, 0).eq(toBigWithDefault(value.price, 0))}
      currency={value.amountCurrency}
      warning={
        value.priceField.errorString ? (
          <FormMessage error>{value.priceField.errorString}</FormMessage>
        ) : priceIsOff && computedPx ? (
          <FormMessage warning>
            Differs from computed price <InlineFormattedNumber number={computedPx} currency={value.amountCurrency} />
          </FormMessage>
        ) : undefined
      }
    >
      <NumberInput
        value={value.priceField.displayValue}
        disabled={value.priceField.isDisabled}
        invalid={!!value.priceField.errorString}
        onChange={handleChange}
        data-testid={`modify-fixing-blotter-price-input-${value.rowID}`}
        suffix={value.amountCurrency}
        prefix={
          priceIsOff && computedPx ? (
            <Tooltip tooltip="Set to computed price">
              <IconButton
                ghost
                size={FormControlSizes.Small}
                onClick={() => {
                  if (!value.symbol) {
                    return;
                  }

                  onEntryPriceChange(value.symbol, computedPx.toFixed());
                }}
                icon={IconName.Refresh}
                data-testid={`modify-fixing-blotter-price-refresh-button-${value.rowID}`}
                tabIndex={-1}
              />
            </Tooltip>
          ) : undefined
        }
      />
    </InputCell>
  );
}

function AmountInputCellRenderer({
  value,
  node,
  onEntryAmountChange,
}: ICellRendererParams<ModifyFixingEntry, ModifyFixingEntry> & EntryInputCellRendererParams) {
  const details = useEntryPricingDetails(value ?? undefined);
  const computedAmt = details?.amt;
  const amountIsOff = computedAmt ? !value?.amountField.bigValue?.eq(computedAmt) : false;

  const handleChange = useCallbackRef((amount: string) => {
    if (!value) {
      return;
    }

    if (!value.symbol) {
      return;
    }

    onEntryAmountChange(value.symbol, amount);
  });

  if (!value) {
    return <></>;
  }

  return (
    <InputCell
      initialValue={value.amount}
      showInitialValue={!toBigWithDefault(value.amountField.value, 0).eq(toBigWithDefault(value.amount, 0))}
      currency={value.amountCurrency}
      warning={
        value.amountField.errorString ? (
          <FormMessage error>{value.amountField.errorString}</FormMessage>
        ) : amountIsOff && computedAmt ? (
          <FormMessage warning>
            Differs from computed amount <InlineFormattedNumber number={computedAmt} currency={value.amountCurrency} />
          </FormMessage>
        ) : undefined
      }
    >
      <NumberInput
        value={value.amountField.displayValue}
        disabled={value.amountField.isDisabled}
        invalid={!!value.amountField.errorString}
        onChange={handleChange}
        id={modifyTotalInputID(node.rowIndex)}
        data-testid={`modify-fixing-blotter-amount-input-${value.rowID}`}
        suffix={value.amountCurrency}
        prefix={
          amountIsOff && computedAmt ? (
            <Tooltip tooltip="Set to computed amount">
              <IconButton
                ghost
                size={FormControlSizes.Small}
                onClick={() => {
                  if (!value.symbol) {
                    return;
                  }

                  onEntryAmountChange(value.symbol, computedAmt.toFixed());
                }}
                icon={IconName.Refresh}
                data-testid={`modify-fixing-blotter-amount-refresh-button-${value.rowID}`}
                tabIndex={-1}
              />
            </Tooltip>
          ) : undefined
        }
      />
    </InputCell>
  );
}

function InputCell({
  children,
  initialValue,
  showInitialValue,
  currency,
  warning,
}: {
  children: ReactNode;
  initialValue?: string;
  showInitialValue: boolean;
  currency?: string;
  warning?: ReactNode;
}) {
  return (
    <Stack h="100%" flexBasis="100%" justifyContent="stretch" gap="spacing2">
      <Flex flex={1} alignItems="flex-end" justifyContent="flex-end" mb="spacing2">
        {showInitialValue ? <InitialValueText value={initialValue} currency={currency} /> : null}
      </Flex>
      <Flex alignItems="center" justifyContent="center">
        {children}
      </Flex>
      <Flex flex={1} alignItems="flex-start" justifyContent="flex-end">
        <CellMessage warning={warning} />
      </Flex>
    </Stack>
  );
}

function CellMessage({ warning }: { warning?: ReactNode }) {
  return (
    <Text textAlign="left" visibility={warning ? 'visible' : 'hidden'}>
      {warning}
    </Text>
  );
}

function useEntryPricingDetails(entry: ModifyFixingEntry | undefined) {
  const security = useSecurity(entry?.symbol);
  const amountCurrency = useCurrency(entry?.amountCurrency);

  return useMemo(
    () =>
      getPricingDetailsFromFixing({
        fixingPrice: entry?.fixingField.value,
        quote: entry
          ? {
              TradedSide: entry.tradedSide,
              TradedQty: entry.tradedQty,
              PricingReference: entry.PricingReference,
            }
          : undefined,
        priceIncrement: security?.MinPriceIncrement,
        amountIncrement: amountCurrency?.MinIncrement,
      }),
    [entry, security?.MinPriceIncrement, amountCurrency?.MinIncrement]
  );
}
